import { API, APIError } from ".";
import type { OpenAPI } from "../../interfaces";
import SearchParams from "../searchParams";

export const discussions = (api: API) => ({
  getAll(
    forType: string,
    forId: string,
    success: (
      data: OpenAPI.GET<"/discussions/{for_type}/{for_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/discussions/${forType}/${forId}`,
      null,
      success,
      fail,
    );
  },
  create(
    forType: string,
    forId: string,
    data: OpenAPI.POST<"/discussions/{for_type}/{for_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/discussions/{for_type}/{for_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/discussions/${forType}/${forId}`,
      data,
      success,
      fail,
    );
  },
  get(
    discussionId: string,
    success: (
      data: OpenAPI.GET<"/discussions/{discussion_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/discussions/${discussionId}`,
      null,
      success,
      fail,
    );
  },
  update(
    discussionId: string,
    data: OpenAPI.PUT<"/discussions/{discussion_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/discussions/{discussion_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/discussions/${discussionId}`,
      data,
      success,
      fail,
    );
  },
  delete(
    discussionId: string,
    success: (
      data: OpenAPI.DELETE<"/discussions/{discussion_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/discussions/${discussionId}`,
      null,
      success,
      fail,
    );
  },
  getComments(
    discussionId: string,
    params: OpenAPI.GET<"/discussions/{discussion_id}/comments">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/discussions/{discussion_id}/comments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/discussions/${discussionId}/comments?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getCommentIndex(
    discussionId: string,
    commentId: string,
    success: (
      data: OpenAPI.GET<"/discussions/{discussion_id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/discussions/${discussionId}/comments/${commentId}`,
      null,
      success,
      fail,
    );
  },
  getMentionable(
    discussionId: string,
    query: string,
    success: (
      data: OpenAPI.GET<"/discussions/{discussion_id}/mentionable">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/discussions/${discussionId}/mentionable?query=${query}`,
      null,
      success,
      fail,
    );
  },
  addComment(
    discussionId: string,
    data: OpenAPI.POST<"/discussions/{discussion_id}/comments">["requestBody"],
    success: (
      data: OpenAPI.POST<"/discussions/{discussion_id}/comments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/discussions/${discussionId}/comments`,
      data,
      success,
      fail,
    );
  },
  deleteComment(
    discussionId: string,
    commentId: string,
    success: (
      data: OpenAPI.DELETE<"/discussions/{discussion_id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/discussions/${discussionId}/comments/${commentId}`,
      null,
      success,
      fail,
    );
  },
  approveComment(
    discussionId: string,
    commentId: string,
    success: (
      data: OpenAPI.POST<"/discussions/{discussion_id}/comments/{comment_id}/approve">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/discussions/${discussionId}/comments/${commentId}/approve`,
      null,
      success,
      fail,
    );
  },
  rejectComment(
    discussionId: string,
    commentId: string,
    success: (
      data: OpenAPI.POST<"/discussions/{discussion_id}/comments/{comment_id}/reject">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/discussions/${discussionId}/comments/${commentId}/reject`,
      null,
      success,
      fail,
    );
  },
  editComment(
    discussionId: string,
    commentId: string,
    data: OpenAPI.PUT<"/discussions/{discussion_id}/comments/{comment_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/discussions/{discussion_id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/discussions/${discussionId}/comments/${commentId}`,
      data,
      success,
      fail,
    );
  },
  commentAddReaction(
    discussionId: string,
    commentId: string,
    emoji: string,
    success: (
      data: OpenAPI.PUT<"/discussions/{discussion_id}/comments/{comment_id}/reacts/{emoji}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/discussions/${discussionId}/comments/${commentId}/reacts/${emoji}`,
      null,
      success,
      fail,
    );
  },
  commentRemoveReaction(
    discussionId: string,
    commentId: string,
    emoji: string,
    success: (
      data: OpenAPI.DELETE<"/discussions/{discussion_id}/comments/{comment_id}/reacts/{emoji}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/discussions/${discussionId}/comments/${commentId}/reacts/${emoji}`,
      null,
      success,
      fail,
    );
  },
  voteOnCommentPoll(
    discussionId: string,
    commentId: string,
    pollId: string,
    data: OpenAPI.POST<"/discussions/{discussion_id}/comments/{comment_id}/polls/{poll_id}/vote">["requestBody"],
    success: (
      data: OpenAPI.POST<"/discussions/{discussion_id}/comments/{comment_id}/polls/{poll_id}/vote">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/discussions/${discussionId}/comments/${commentId}/polls/${pollId}/vote`,
      data,
      success,
      fail,
    );
  },
});
